import { useCallback, useEffect, useId, useState } from 'react'
import { Link } from 'react-router-dom'

import { useOptionalAppAppearance } from '@/context/AppAppearanceContext'
import { useAuthSession } from '@/features/auth'
import { COMMAND_DECK_PREVIEW_ROUTE_PATH } from '../config/commandDeckMvp'
import type { CommandDeckPreviewBundle, CommandDeckSurfaceMode } from '../contracts/types'
import {
  type CommandDeckCockpitPrefs,
  type CommandDeckDefaultSurfacePref,
  type CommandDeckDensity,
  type CommandDeckGsEntryPref,
  type CommandDeckMotionPref,
  describeDefaultSurfacePref,
  describeGsEntryPref,
  loadCommandDeckCockpitPrefs,
  saveCommandDeckCockpitPrefs,
} from '../lib/commandDeckCockpitPrefs'
import { getAccessLabel } from './missionControlModel'

type SettingsSectionId = 'operator' | 'access' | 'display' | 'cockpit' | 'notifications' | 'advanced'

const SECTIONS: { id: SettingsSectionId; label: string }[] = [
  { id: 'operator', label: 'Operator' },
  { id: 'access', label: 'Access' },
  { id: 'display', label: 'Display' },
  { id: 'cockpit', label: 'Cockpit' },
  { id: 'notifications', label: 'Notifications' },
  { id: 'advanced', label: 'Advanced' },
]

const PREVIEW_PROFILE_LABELS: Record<string, string> = {
  visitor: 'Visitor',
  creator_beta: 'Creator Beta (ORBIS)',
  revoked: 'Revoked',
}

interface Props {
  open: boolean
  onClose: () => void
  bundle: CommandDeckPreviewBundle
  mode: CommandDeckSurfaceMode
  onPrefsChange?: (prefs: CommandDeckCockpitPrefs) => void
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return 'ST'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return `${parts[0][0] ?? ''}${parts[1][0] ?? ''}`.toUpperCase()
}

function formatReadiness(value: string): string {
  return value.replace(/_/g, ' ')
}

export function CommandDeckSettingsDrawer({
  open,
  onClose,
  bundle,
  mode,
  onPrefsChange,
}: Props) {
  const panelId = useId()
  const isPreview = mode === 'preview'
  const appearance = useOptionalAppAppearance()
  const { signOut, status: authStatus } = useAuthSession()
  const { identity } = bundle.personalization
  const name = identity.displayName

  const [activeSection, setActiveSection] = useState<SettingsSectionId>('operator')
  const [prefs, setPrefs] = useState<CommandDeckCockpitPrefs>(() => loadCommandDeckCockpitPrefs())
  const [saveNote, setSaveNote] = useState<string | null>(null)

  useEffect(() => {
    if (open) {
      setPrefs(loadCommandDeckCockpitPrefs())
      setSaveNote(null)
    }
  }, [open])

  const persistPrefs = useCallback(
    (next: CommandDeckCockpitPrefs) => {
      const saved = saveCommandDeckCockpitPrefs(next)
      setPrefs(saved)
      onPrefsChange?.(saved)
      setSaveNote('Saved on this device. Preferences do not run workflows or change server access.')
    },
    [onPrefsChange]
  )

  const patchPrefs = useCallback(
    (patch: Partial<CommandDeckCockpitPrefs>) => {
      persistPrefs({ ...prefs, ...patch })
    },
    [persistPrefs, prefs]
  )

  if (!open) return null

  const access = bundle.access
  const missionName = bundle.progress.projectName
  const activeSurfaceLabel =
    bundle.personalization.surface.surfaceId !== 'none'
      ? bundle.personalization.surface.label
      : 'Unresolved'

  return (
    <div className="cd-mc-drawer-backdrop" role="presentation" onClick={onClose}>
      <aside
        className="cd-mc-drawer command-deck-glass"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cd-mc-drawer-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="cd-mc-drawer__header">
          <div>
            <h2 id="cd-mc-drawer-title">Cockpit Settings</h2>
            <p className="cd-mc-drawer__subtitle">Engine control panel — not account billing or security</p>
          </div>
          <button
            type="button"
            className="cd-mc-drawer__close"
            onClick={onClose}
            aria-label="Close cockpit settings"
          >
            <span className="cd-mc-drawer__close-icon" aria-hidden="true">
              ×
            </span>
            <span className="cd-mc-drawer__close-label">Close</span>
          </button>
        </header>

        {saveNote ? (
          <p className="cd-mc-drawer__save-note" role="status">
            {saveNote}
          </p>
        ) : null}

        <div className="cd-mc-drawer__layout">
          <nav className="cd-mc-drawer__nav" aria-label="Settings sections">
            {SECTIONS.map((section) => (
              <button
                key={section.id}
                type="button"
                className={`cd-mc-drawer__nav-item${activeSection === section.id ? ' cd-mc-drawer__nav-item--active' : ''}`}
                aria-current={activeSection === section.id ? 'true' : undefined}
                onClick={() => setActiveSection(section.id)}
              >
                {section.label}
              </button>
            ))}
          </nav>

          <div className="cd-mc-drawer__panel" id={panelId} role="region" aria-label={activeSection}>
            {activeSection === 'operator' ? (
              <div className="cd-mc-settings-section">
                <h3>Operator</h3>
                <div className="cd-mc-settings-operator">
                  <div className="cd-mc-settings-avatar" aria-hidden="true">
                    {initials(name)}
                  </div>
                  <dl className="cd-mc-settings-dl">
                    <div>
                      <dt>Display name</dt>
                      <dd>{name}</dd>
                    </div>
                    <div>
                      <dt>Role</dt>
                      <dd>{identity.roleDisplayLabel}</dd>
                    </div>
                    <div>
                      <dt>Status</dt>
                      <dd>{identity.operationalStatus}</dd>
                    </div>
                  </dl>
                </div>
                <p className="cd-mc-settings-hint">
                  Avatar upload is not available in this build. Profile image changes require a separate
                  authorized lane.
                </p>
              </div>
            ) : null}

            {activeSection === 'access' ? (
              <div className="cd-mc-settings-section">
                <h3>Access</h3>
                <p className="cd-mc-settings-hint">
                  Read-only snapshot from your session. Settings cannot grant studio access or change
                  entitlements.
                </p>
                <dl className="cd-mc-settings-dl">
                  <div>
                    <dt>Access state</dt>
                    <dd>{getAccessLabel(access.accessState)}</dd>
                  </div>
                  <div>
                    <dt>Account readiness</dt>
                    <dd>{formatReadiness(access.accountReadiness)}</dd>
                  </div>
                  <div>
                    <dt>Service mode</dt>
                    <dd>{access.serviceAccessMode}</dd>
                  </div>
                  <div>
                    <dt>Active mission</dt>
                    <dd>{missionName}</dd>
                  </div>
                  <div>
                    <dt>Active surface</dt>
                    <dd>{activeSurfaceLabel}</dd>
                  </div>
                </dl>
              </div>
            ) : null}

            {activeSection === 'display' ? (
              <div className="cd-mc-settings-section">
                <h3>Display</h3>
                <p className="cd-mc-settings-hint">Applies to this device only. Does not sync to Firebase.</p>

                {appearance ? (
                  <fieldset className="cd-mc-settings-fieldset">
                    <legend>Theme</legend>
                    {(['system', 'light', 'dark'] as const).map((themeMode) => (
                      <label key={themeMode} className="cd-mc-settings-radio">
                        <input
                          type="radio"
                          name="cd-theme"
                          checked={appearance.mode === themeMode}
                          onChange={() => appearance.setMode(themeMode)}
                        />
                        {themeMode === 'system' ? 'Follow system' : themeMode}
                      </label>
                    ))}
                  </fieldset>
                ) : (
                  <p className="cd-mc-settings-hint">Theme controls are unavailable in this shell context.</p>
                )}

                <fieldset className="cd-mc-settings-fieldset">
                  <legend>Cockpit motion</legend>
                  {(
                    [
                      ['system', 'Follow system reduced-motion'],
                      ['reduce', 'Reduce cockpit animations'],
                    ] as const
                  ).map(([value, label]) => (
                    <label key={value} className="cd-mc-settings-radio">
                      <input
                        type="radio"
                        name="cd-motion"
                        checked={prefs.motion === value}
                        onChange={() => patchPrefs({ motion: value as CommandDeckMotionPref })}
                      />
                      {label}
                    </label>
                  ))}
                </fieldset>

                <fieldset className="cd-mc-settings-fieldset">
                  <legend>Density</legend>
                  {(
                    [
                      ['comfortable', 'Comfortable'],
                      ['compact', 'Compact'],
                    ] as const
                  ).map(([value, label]) => (
                    <label key={value} className="cd-mc-settings-radio">
                      <input
                        type="radio"
                        name="cd-density"
                        checked={prefs.density === value}
                        onChange={() => patchPrefs({ density: value as CommandDeckDensity })}
                      />
                      {label}
                    </label>
                  ))}
                </fieldset>
              </div>
            ) : null}

            {activeSection === 'cockpit' ? (
              <div className="cd-mc-settings-section">
                <h3>Cockpit</h3>
                <p className="cd-mc-settings-hint">
                  Preferences only. Saving does not navigate, compile, hand off, or execute workflows.
                  Continue remains the honest route control in Mission Control.
                </p>

                <fieldset className="cd-mc-settings-fieldset">
                  <legend>Default surface (preference)</legend>
                  {(
                    [
                      ['command_deck', 'Command Deck'],
                      ['guided_studio', 'Guided Studio'],
                      ['remember_last', 'Remember last surface'],
                    ] as const
                  ).map(([value, label]) => (
                    <label key={value} className="cd-mc-settings-radio">
                      <input
                        type="radio"
                        name="cd-default-surface"
                        checked={prefs.defaultSurface === value}
                        onChange={() =>
                          patchPrefs({ defaultSurface: value as CommandDeckDefaultSurfacePref })
                        }
                      />
                      {label}
                    </label>
                  ))}
                </fieldset>
                <p className="cd-mc-settings-meta">
                  Current preference: {describeDefaultSurfacePref(prefs.defaultSurface)}
                </p>

                <fieldset className="cd-mc-settings-fieldset">
                  <legend>Guided Studio entry (preference)</legend>
                  {(
                    [
                      ['intake', 'Start at intake'],
                      ['resume_last', 'Resume last session when available'],
                    ] as const
                  ).map(([value, label]) => (
                    <label key={value} className="cd-mc-settings-radio">
                      <input
                        type="radio"
                        name="cd-gs-entry"
                        checked={prefs.gsEntry === value}
                        onChange={() => patchPrefs({ gsEntry: value as CommandDeckGsEntryPref })}
                      />
                      {label}
                    </label>
                  ))}
                </fieldset>
                <p className="cd-mc-settings-meta">Current preference: {describeGsEntryPref(prefs.gsEntry)}</p>
              </div>
            ) : null}

            {activeSection === 'notifications' ? (
              <div className="cd-mc-settings-section">
                <h3>Notifications</h3>
                <p className="cd-mc-settings-hint">Notification delivery is not connected in this build.</p>
                <fieldset className="cd-mc-settings-fieldset" disabled>
                  <legend>Channels</legend>
                  <label className="cd-mc-settings-radio">
                    <input type="checkbox" disabled />
                    Email alerts
                  </label>
                  <label className="cd-mc-settings-radio">
                    <input type="checkbox" disabled />
                    In-app notices
                  </label>
                </fieldset>
                <p className="cd-mc-settings-soon">Not available — no backend wiring on Command Deck v1</p>
              </div>
            ) : null}

            {activeSection === 'advanced' ? (
              <div className="cd-mc-settings-section">
                <h3>Advanced</h3>
                {isPreview ? (
                  <>
                    <p className="cd-mc-settings-hint">Preview mode — switch access profile from the deck banner.</p>
                    <dl className="cd-mc-settings-dl">
                      <div>
                        <dt>Preview profile</dt>
                        <dd>
                          {PREVIEW_PROFILE_LABELS[bundle.accessProfileId] ?? bundle.accessProfileId}
                        </dd>
                      </div>
                    </dl>
                    <p className="cd-mc-settings-links">
                      {Object.entries(PREVIEW_PROFILE_LABELS).map(([id, label]) => (
                        <Link
                          key={id}
                          to={`${COMMAND_DECK_PREVIEW_ROUTE_PATH}?accessProfile=${id}`}
                        >
                          {label}
                        </Link>
                      ))}
                    </p>
                  </>
                ) : (
                  <p className="cd-mc-settings-hint">
                    Live session — access profile is determined by authentication, not this panel.
                  </p>
                )}

                {!isPreview && authStatus === 'authenticated' ? (
                  <button
                    type="button"
                    className="cd-mc-settings-signout"
                    onClick={() => {
                      void signOut()
                    }}
                  >
                    Sign out
                  </button>
                ) : null}

                <p className="cd-mc-settings-hint">
                  Billing, password, subscription, and service grants are intentionally not shown here.
                </p>
              </div>
            ) : null}
          </div>
        </div>
      </aside>
    </div>
  )
}
